import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useState } from 'react';
import { LINKS } from '@/constants/links';
import { Button } from '@/components/buttons';
import { useTranslation } from 'react-i18next';
import { CalculatorForm } from './CalculatorForm';
import { Modal } from '@/components/modal';
import { formatAmount } from '@/utils/formatting';
import { Language } from '@/constants/currencyConfig';
import { SectionHeadline } from '@/components/SectionHeadline';
import { useCalculator } from './useCalculator';
import { formatApiAmount } from '@/utils/amount';
import { CalculatorResultRow } from '@/components/CalculatorResultRow';
import { mergeClasses } from '@/utils/style';
const PRINCIPAL_MAX = 100_000;
const PRINCIPAL_MIN = 1_500;
const DEFAULT_SELECTED_PRINCIPAL = 50_000;
export type CalculatorType = 'installments' | 'loan';
interface CalculatorProps {
  compact?: boolean;
  type?: CalculatorType;
}
export function Calculator(props) {
  const $ = _c(117);
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [maxPrincipal, setMaxPrincipal] = useState(DEFAULT_SELECTED_PRINCIPAL);
  const t0 = props.type === "loan" ? "loan" : "installments";
  let t1;
  if ($[0] !== t0) {
    t1 = [t0];
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    t,
    i18n
  } = useTranslation(t1);
  const [paymentsCount, setPaymentsCount] = useState(10);
  const calculatorId = props.type === "loan" ? "loanCalculator" : "installmentsCalculator";
  const registrationId = props.type === "loan" && props.compact ? "loansHeroPrimaryCTA" : `${calculatorId}Registration`;
  const exampleId = `${calculatorId}Example`;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = t3 => {
      const {
        maxPrincipal: selectedPrincipal,
        paymentsCount: selectedPaymentsCount
      } = t3;
      setMaxPrincipal(selectedPrincipal);
      setPaymentsCount(selectedPaymentsCount);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const calculate = t2;
  const calculator = useCalculator(maxPrincipal * 100, paymentsCount);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setIsModalOpened(false);
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t) {
    t4 = t("calculator.example.title");
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = <h2 className="mb-6 text-xl font-semibold">{t4}</h2>;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t) {
    t6 = t("calculator.example.text.1");
    $[8] = t;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = <p className="mb-4 text-sm sm:text-base">{t6}</p>;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t) {
    t8 = t("calculator.example.subtitle");
    $[12] = t;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = <h3 className="mb-1 text-sm font-semibold sm:text-base">{t8}</h3>;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t) {
    t10 = t("calculator.example.text.2");
    $[16] = t;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10) {
    t11 = <p className="mb-4 text-sm sm:text-base">{t10}</p>;
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== exampleId || $[21] !== isModalOpened || $[22] !== t11 || $[23] !== t5 || $[24] !== t7 || $[25] !== t9) {
    t12 = <Modal isVisible={isModalOpened} onClose={t3} modalName={exampleId} analyticsParam={exampleId} testId={exampleId} className="max-w-[48rem] rounded-4xl p-10">{t5}{t7}{t9}{t11}</Modal>;
    $[20] = exampleId;
    $[21] = isModalOpened;
    $[22] = t11;
    $[23] = t5;
    $[24] = t7;
    $[25] = t9;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const t13 = props.compact && "px-0";
  let t14;
  if ($[27] !== t13) {
    t14 = mergeClasses("relative z-10 px-2", t13);
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== props.compact || $[30] !== props.type || $[31] !== t) {
    t15 = !props.compact && <SectionHeadline className="mb-10 px-4 md:mb-16" variant="inverted" anchorKey={`${props.type === "loan" ? "loan" : "installments"}.calculator.title`}>{t("calculator.title")}</SectionHeadline>;
    $[29] = props.compact;
    $[30] = props.type;
    $[31] = t;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  const t16 = props.compact && "max-w-[38.5rem] md:p-10 xl:p-12";
  let t17;
  if ($[33] !== t16) {
    t17 = mergeClasses("mx-auto max-w-[60.5rem] rounded-2xl bg-blue-100 p-4 text-gray-900 md:rounded-[2.5rem] md:p-12 md:pt-14", t16);
    $[33] = t16;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== calculator.installments || $[36] !== calculator.isLoading || $[37] !== props.type) {
    t18 = <CalculatorForm type={props.type} principalMax={PRINCIPAL_MAX} principalMin={PRINCIPAL_MIN} onChange={calculate} installments={calculator.installments} isLoading={calculator.isLoading} />;
    $[35] = calculator.installments;
    $[36] = calculator.isLoading;
    $[37] = props.type;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  const t19 = props.compact && "md:mb-10";
  let t20;
  if ($[39] !== t19) {
    t20 = mergeClasses("mt-12 mb-4 flex flex-col items-center justify-between gap-3 rounded-2xl bg-blue-500 p-6 text-white md:mb-12", t19);
    $[39] = t19;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== t) {
    t21 = t("calculator.table.monthly");
    $[41] = t;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== calculator.selectedPlan?.installmentValue?.amount || $[44] !== i18n.language) {
    t22 = formatAmount(formatApiAmount(calculator?.selectedPlan?.installmentValue?.amount || 0), i18n.language as Language);
    $[43] = calculator.selectedPlan?.installmentValue?.amount;
    $[44] = i18n.language;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== t21 || $[47] !== t22) {
    t23 = <CalculatorResultRow highlighted={true} title={t21} value={t22} />;
    $[46] = t21;
    $[47] = t22;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] !== t) {
    t24 = t("calculator.table.plusYouPay");
    $[49] = t;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] !== calculator.selectedPlan?.totalInterestValue?.amount || $[52] !== i18n.language) {
    t25 = formatAmount(formatApiAmount(calculator?.selectedPlan?.totalInterestValue?.amount || 0), i18n.language as Language);
    $[51] = calculator.selectedPlan?.totalInterestValue?.amount;
    $[52] = i18n.language;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== t24 || $[55] !== t25) {
    t26 = <CalculatorResultRow highlighted={true} title={t24} value={t25} />;
    $[54] = t24;
    $[55] = t25;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] !== t) {
    t27 = t("calculator.table.inTotal");
    $[57] = t;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== calculator.selectedPlan?.totalClaim?.amount || $[60] !== i18n.language) {
    t28 = formatAmount(formatApiAmount(calculator?.selectedPlan?.totalClaim?.amount || 0), i18n.language as Language);
    $[59] = calculator.selectedPlan?.totalClaim?.amount;
    $[60] = i18n.language;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== t27 || $[63] !== t28) {
    t29 = <CalculatorResultRow highlighted={true} title={t27} value={t28} />;
    $[62] = t27;
    $[63] = t28;
    $[64] = t29;
  } else {
    t29 = $[64];
  }
  let t30;
  if ($[65] !== t) {
    t30 = t("calculator.table.annualInterestRate");
    $[65] = t;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  const t31 = `${calculator.selectedPlan?.interestRate || 0} %`;
  let t32;
  if ($[67] !== t30 || $[68] !== t31) {
    t32 = <CalculatorResultRow title={t30} value={t31} />;
    $[67] = t30;
    $[68] = t31;
    $[69] = t32;
  } else {
    t32 = $[69];
  }
  let t33;
  if ($[70] !== t) {
    t33 = t("calculator.table.rpsn");
    $[70] = t;
    $[71] = t33;
  } else {
    t33 = $[71];
  }
  const t34 = `${calculator.selectedPlan?.rpsn || 0} %`;
  let t35;
  if ($[72] !== t33 || $[73] !== t34) {
    t35 = <CalculatorResultRow title={t33} value={t34} />;
    $[72] = t33;
    $[73] = t34;
    $[74] = t35;
  } else {
    t35 = $[74];
  }
  let t36;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = LINKS();
    $[75] = t36;
  } else {
    t36 = $[75];
  }
  const t37 = t36.registration;
  let t38;
  if ($[76] !== t) {
    t38 = t("calculator.cta");
    $[76] = t;
    $[77] = t38;
  } else {
    t38 = $[77];
  }
  let t39;
  if ($[78] !== registrationId || $[79] !== t38) {
    t39 = <Button to={t37} testId={registrationId} analyticsParam={registrationId} size="lg" variant="primary" align="center" className="mt-6 mb-3 max-w-full md:hidden lg:mt-0">{t38}</Button>;
    $[78] = registrationId;
    $[79] = t38;
    $[80] = t39;
  } else {
    t39 = $[80];
  }
  let t40;
  if ($[81] !== t20 || $[82] !== t23 || $[83] !== t26 || $[84] !== t29 || $[85] !== t32 || $[86] !== t35 || $[87] !== t39) {
    t40 = <div className={t20}>{t23}{t26}{t29}{t32}{t35}{t39}</div>;
    $[81] = t20;
    $[82] = t23;
    $[83] = t26;
    $[84] = t29;
    $[85] = t32;
    $[86] = t35;
    $[87] = t39;
    $[88] = t40;
  } else {
    t40 = $[88];
  }
  let t41;
  if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = LINKS();
    $[89] = t41;
  } else {
    t41 = $[89];
  }
  const t42 = t41.registration;
  let t43;
  if ($[90] !== t) {
    t43 = t("calculator.cta");
    $[90] = t;
    $[91] = t43;
  } else {
    t43 = $[91];
  }
  let t44;
  if ($[92] !== registrationId || $[93] !== t43) {
    t44 = <Button to={t42} testId={registrationId} analyticsParam={registrationId} size="lg" variant="primary" align="center" className="mb-3 hidden md:block">{t43}</Button>;
    $[92] = registrationId;
    $[93] = t43;
    $[94] = t44;
  } else {
    t44 = $[94];
  }
  let t45;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = () => setIsModalOpened(true);
    $[95] = t45;
  } else {
    t45 = $[95];
  }
  let t46;
  if ($[96] !== t) {
    t46 = t("calculator.example.cta");
    $[96] = t;
    $[97] = t46;
  } else {
    t46 = $[97];
  }
  let t47;
  if ($[98] !== exampleId || $[99] !== t46) {
    t47 = <div className="text-center"><Button variant="unstyled" onClick={t45} testId={exampleId} analyticsParam={exampleId} align="center" className="text-blue-500 underline">{t46}</Button></div>;
    $[98] = exampleId;
    $[99] = t46;
    $[100] = t47;
  } else {
    t47 = $[100];
  }
  let t48;
  if ($[101] !== t17 || $[102] !== t18 || $[103] !== t40 || $[104] !== t44 || $[105] !== t47) {
    t48 = <div className={t17}>{t18}{t40}{t44}{t47}</div>;
    $[101] = t17;
    $[102] = t18;
    $[103] = t40;
    $[104] = t44;
    $[105] = t47;
    $[106] = t48;
  } else {
    t48 = $[106];
  }
  let t49;
  if ($[107] !== t14 || $[108] !== t15 || $[109] !== t48) {
    t49 = <div className={t14}>{t15}{t48}</div>;
    $[107] = t14;
    $[108] = t15;
    $[109] = t48;
    $[110] = t49;
  } else {
    t49 = $[110];
  }
  let t50;
  if ($[111] !== props.compact) {
    t50 = !props.compact && <div className="bgCircle absolute top-[10%] left-2/3" />;
    $[111] = props.compact;
    $[112] = t50;
  } else {
    t50 = $[112];
  }
  let t51;
  if ($[113] !== t12 || $[114] !== t49 || $[115] !== t50) {
    t51 = <>{t12}{t49}{t50}</>;
    $[113] = t12;
    $[114] = t49;
    $[115] = t50;
    $[116] = t51;
  } else {
    t51 = $[116];
  }
  return t51;
}